'use client'

import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { FC, useEffect, useRef, useState } from 'react'

import { spaceGrotesk } from '@/app/fonts'
import BookingTile from '@/components/AnimatedTransactions/BookingTile'
import Receipt from '@/components/AnimatedTransactions/Receipt'
import FullScreenOnMobileWrapper from '@/components/FullScreenOnMobileWrapper'
import useDevice from '@/hooks/useDevice'

// eslint-disable-next-line complexity
const AnimatedTransactions: FC = () => {
    const { mdScreen, smScreen } = useDevice()

    const [startAnimation, setStartAnimation] = useState(false)
    const [showBookingTile, setShowBookingTile] = useState(false)
    const [showReceipt, setShowReceipt] = useState(false)

    const componentRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setStartAnimation(true)
                    setTimeout(() => {
                        setShowBookingTile(true)
                    }, 500)
                    setTimeout(() => {
                        setShowReceipt(true)
                    }, 2850)
                }
            },
            {
                threshold: 0.1,
            },
        )

        if (componentRef.current) {
            observer.observe(componentRef.current)
        }

        return () => {
            if (componentRef.current) {
                observer.unobserve(componentRef.current)
            }
        }
    }, [])

    return (
        <FullScreenOnMobileWrapper>
            <Box
                sx={{
                    display: 'flex',
                    gap: smScreen ? 6 : 9,
                    flexDirection: 'column',
                    fontFamily: `${spaceGrotesk.style.fontFamily}`,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: smScreen ? 6 : 3,
                        flexDirection: smScreen ? 'column' : 'row',
                        ...(smScreen && {
                            pl: 3,
                            pr: 3,
                        }),
                    }}
                >
                    <Box
                        sx={{
                            flex: '1 1 auto',
                            width: '100%',
                        }}
                    >
                        <Text
                            variant={mdScreen ? 'h5' : 'h4'}
                            sx={{ mb: smScreen ? 2 : 6, fontWeight: 700 }}
                        >
                            Calculate using transaction data
                        </Text>
                        <Text variant={mdScreen ? 'body2' : 'body1'}>
                            Lune uses Merchant Category Codes (MCC) to give your customers instant
                            visibility into the emissions of every card transaction to help them
                            begin their emissions reporting journey.
                        </Text>
                    </Box>
                    <Box
                        sx={{
                            flex: '1 1 auto',
                            width: '100%',
                        }}
                    >
                        <Text
                            variant={mdScreen ? 'h5' : 'h4'}
                            sx={{ mb: smScreen ? 2 : 6, fontWeight: 700 }}
                        >
                            Enhance accuracy using line items
                        </Text>
                        <Text variant={mdScreen ? 'body2' : 'body1'}>
                            Lune enhances calculation accuracy using AI to automatically match
                            relevant emission factors with line item data extracted from invoices,
                            receipts, and other transaction documents.
                        </Text>
                    </Box>
                </Box>
                <Box
                    ref={componentRef}
                    sx={{
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 3,
                        height: {
                            xs: '640px',
                            sm: '800px',
                        },
                        borderRadius: smScreen ? 'none' : '40px',
                        backgroundImage: `url('/images/green-grid.png')`,
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        p: 10,
                        position: 'relative',
                        pl: {
                            xs: 6,
                            md: 10,
                        },
                        pr: {
                            xs: 6,
                            md: 10,
                        },

                        '.MuiTypography-root': {
                            fontFamily: `${spaceGrotesk.style.fontFamily} !important`,
                        },

                        ...(smScreen && {
                            '.MuiTypography-h5': {
                                fontSize: '13px !important',
                                lineHeight: '17px !important',
                            },
                            '.MuiTypography-body1, .MuiTypography-body2': {
                                fontSize: '8px !important',
                                lineHeight: '12px !important',
                            },
                        }),
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: {
                                xs: 6,
                                sm: 10,
                            },
                            flexDirection: 'column',
                        }}
                    >
                        {showBookingTile && (
                            <Box
                                sx={{
                                    height: '141px',
                                    width: '100%',
                                    opacity: 0,
                                    animation: 'show-tile .3s ease-out 0s forwards',

                                    '@keyframes show-tile': {
                                        '0%': {
                                            opacity: 0,
                                        },
                                        '100%': {
                                            opacity: 1,
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        ...(startAnimation && {
                                            position: 'absolute',
                                            left: '48px',
                                            right: '48px',
                                            margin: 'auto',
                                            animation: 'move .3s ease-out 2.3s forwards',
                                            zIndex: '999',

                                            '@keyframes move': {
                                                '0%': {
                                                    top: '295px',
                                                },
                                                '65%': {
                                                    top: smScreen ? '60px' : '20px',
                                                },
                                                '75%': {
                                                    top: smScreen ? '60px' : '20px',
                                                },
                                                '100%': {
                                                    top: smScreen ? '80px' : '40px',
                                                },
                                            },
                                        }),
                                    }}
                                >
                                    <BookingTile />
                                </Box>
                            </Box>
                        )}
                        {/* workaround to preload the receipt edge images so the animation doesn't jump
                         /* this block doesn't show in the UI
                         */}
                        <Box
                            sx={{
                                position: 'absolute',
                                height: 0,
                                width: 0,
                                opacity: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <img src={'/images/receipt-cut-top.png'} alt={'hidden'} />
                            <img src={'/images/receipt-cut-middle.png'} alt={'hidden'} />
                            <img src={'/images/receipt-cut-bottom.png'} alt={'hidden'} />
                        </Box>
                        <Box
                            sx={{
                                display: showReceipt ? 'block' : 'none',
                                width: '100%',
                                margin: 'auto',
                                opacity: 0,

                                ...(showReceipt && {
                                    animation: 'showReceipt .3s ease-out forwards',
                                    '@keyframes showReceipt': {
                                        '0%': {
                                            opacity: 0,
                                            marginBottom: '-100px',
                                        },
                                        '100%': {
                                            opacity: 1,
                                            marginBottom: '0px',
                                        },
                                    },
                                }),
                            }}
                        >
                            <Receipt />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </FullScreenOnMobileWrapper>
    )
}

export default AnimatedTransactions
